import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { galleryNavItems } from './galleryNavItems.ts'
import { FileSelectionType, SelectionMode } from '@/hooks/useFileSelection.tsx'
import { Album } from '@/models/album.ts'
import { DriveContact, HeyDrive } from '@/models/drive.ts'
import ContactInfoModal from '@/components/ContactInfoModal.tsx'
import PreSelectionModal from './PreSelectionModal.tsx'
import { isIOSDevice, requestInstall, shouldOfferAppInstall } from '@/controller/pwaInstall.ts'
import { getHomescreenPath } from '@/controller/driveHelper.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { useTrackStat } from '@/contexts/statsScopeContext.tsx'

interface GalleryNavigationProps {
  drive: HeyDrive
  albums: Album[]
  fileSelection: FileSelectionType
  onScrollToAlbum: (album: Album) => void
  contact: DriveContact | null
  isExpanded: boolean
  onClose?: () => void
}

function scrollToContentIfNeeded() {
  const contentEl = document.getElementById('album-content')
  if (contentEl) {
    const rect = contentEl.getBoundingClientRect()
    if (rect.top > window.innerHeight * 0.8) {
      contentEl.scrollIntoView({ behavior: 'smooth' })
    }
  }
}

export default function GalleryNavigation({
  drive,
  albums,
  fileSelection,
  onScrollToAlbum,
  contact,
  isExpanded,
  onClose,
}: GalleryNavigationProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { albumId } = useParams<{ albumId?: string }>()
  const { authenticationToken } = useProfile()
  const track = useTrackStat()
  const [albumsOpen, setAlbumsOpen] = useState(false)
  const contactModalRef = useRef<HTMLDialogElement>(null)
  const preSelectionModalRef = useRef<HTMLDialogElement>(null)
  const [preSelectionMode, setPreSelectionMode] = useState<'download' | 'share'>('download')

  const primaryAlbum = albums.find(a => a.is_primary) || albums[0]
  const visibleNavItems = galleryNavItems.filter(item =>
    !(item.id === 'imageSelection' && drive.is_hide_selection) &&
    !(item.id === 'download' && drive.is_hide_download) &&
    !(item.id === 'share' && drive.is_hide_share) &&
    !(item.id === 'shop' && drive.is_hide_shop) &&
    !(item.id === 'select' && drive.is_hide_download && drive.is_hide_share) &&
    !(item.id === 'installApp' && !shouldOfferAppInstall())
  )

  function handleItemClick(itemId: string) {
    switch (itemId) {
      case 'albums':
        setAlbumsOpen(!albumsOpen)
        return // Don't close nav for expandable items

      case 'select':
        // Cancel existing selection if active, then start new one
        if (fileSelection.isSelecting) {
          fileSelection.setIsSelecting(false)
        }
        setTimeout(() => {
          fileSelection.setSelectionMode(SelectionMode.DEFAULT)
          fileSelection.setIsSelecting(true)
          scrollToContentIfNeeded()
        }, 0)
        break

      case 'download':
        if (fileSelection.isSelecting) {
          fileSelection.setIsSelecting(false)
        }
        setPreSelectionMode('download')
        setTimeout(() => preSelectionModalRef.current?.showModal(), 0)
        break

      case 'share':
        if (fileSelection.isSelecting) {
          fileSelection.setIsSelecting(false)
        }
        setPreSelectionMode('share')
        setTimeout(() => preSelectionModalRef.current?.showModal(), 0)
        break

      case 'imageSelection':
        // Cancel existing selection if active, then start new one
        if (fileSelection.isSelecting) {
          fileSelection.setIsSelecting(false)
        }
        setTimeout(() => {
          fileSelection.setSelectionMode(SelectionMode.IMAGE_SELECTION)
          fileSelection.setIsSelecting(true)
          scrollToContentIfNeeded()
        }, 0)
        break

      case 'contact':
        track('contact_view')
        setTimeout(() => contactModalRef.current?.showModal(), 0)
        break

      case 'shop':
        if (primaryAlbum) {
          navigate(`/d/${drive.id}/${primaryAlbum.id}/shop`)
          window.scrollTo({ top: 0 })
        }
        break

      case 'installApp':
        if (isIOSDevice()) {
          // Same route as everywhere else: reload the drive with the flag that
          // opens the step-by-step guide (there is no install API on iOS).
          navigate(getHomescreenPath(drive.id, albumId, authenticationToken), {
            state: { showAddToHomescreen: true },
          })
        } else {
          void requestInstall(t('common.installAppHint'))
        }
        break
    }

    onClose?.()
  }

  function handleAlbumClick(album: Album) {
    onScrollToAlbum(album)
    onClose?.()
  }

  return (
    <nav className='flex flex-col overflow-y-auto gap-1'>
      {visibleNavItems.map(item => (
        <div key={item.id}>
          {isExpanded ? (
            <Button
              color='ghost'
              className='w-full justify-start gap-3 h-14 min-h-14 px-4 flex-nowrap overflow-hidden'
              onClick={() => handleItemClick(item.id)}
            >
              <item.icon size={33} stroke={1.2} className='flex-shrink-0' />
              <span className='flex-1 text-left truncate'>{item.label}</span>
              {item.id === 'albums' && (
                albumsOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />
              )}
            </Button>
          ) : (
            <button
              className='w-full flex items-center justify-center h-14 rounded-lg hover:bg-base-200 transition-colors'
              onClick={() => handleItemClick(item.id)}
              title={item.label}
            >
              <item.icon size={33} stroke={1.2} />
            </button>
          )}

          {/* Album sub-list */}
          {item.id === 'albums' && albumsOpen && isExpanded && (
            <div className='flex flex-col gap-1 pl-6 mt-1'>
              {albums.map(album => (
                <Button
                  key={album.id}
                  color='ghost'
                  size='sm'
                  className='w-full justify-start h-10 min-h-10 truncate'
                  onClick={() => handleAlbumClick(album)}
                >
                  <span className='truncate'>{album.title}</span>
                </Button>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Contact modal */}
      <ContactInfoModal contact={contact} modalRef={contactModalRef} />

      {/* Single shared pre-selection modal */}
      <PreSelectionModal
        mode={preSelectionMode}
        modalRef={preSelectionModalRef}
        drive={drive}
        albums={albums}
        fileSelection={fileSelection}
      />
    </nav>
  )
}
